// Organization text-styling primitives: the single source of truth for the quotation-mark pair
// table, language-default resolution, positional open/close classification, and line-break
// normalization. Pure and isomorphic — consumed by the editor (browser, via the Trix range API)
// and the liveblog MCP server (Lambda, reporting to agents), and reusable by a future write-path
// enforcement hook. Deliberately no DOM/Trix dependency here.

import type { TextStylingQuotePairId } from '@webng-types/editor';

export interface QuotePair {
  /** Stable id stored in the `text-styling` setting and referenced by the MCP. */
  id: TextStylingQuotePairId;
  /** English display name — used by non-UI consumers (MCP); the editor form labels via i18n. */
  label: string;
  /** Opening string. May carry inner spacing (e.g. the spaced guillemet's narrow no-break space). */
  open: string;
  /** Closing string. May carry inner spacing. */
  close: string;
}

// U+202F NARROW NO-BREAK SPACE — the character French typography puts inside guillemets.
const NNBSP = ' ';
// U+00A0 NO-BREAK SPACE — the other space that can end up inside guillemets from paste.
const NBSP = ' ';

// Curated, id-keyed table. `open`/`close` are full strings so spacing is part of the pair.
export const QUOTE_PAIRS: readonly QuotePair[] = [
  { id: 'straight', label: 'Straight double quotes', open: '"', close: '"' },
  { id: 'english', label: 'English (“ ”)', open: '“', close: '”' },
  { id: 'german', label: 'German („ “)', open: '„', close: '“' },
  { id: 'french-guillemets', label: 'French guillemets (« »)', open: '«', close: '»' },
  {
    id: 'french-guillemets-spaced',
    label: 'French guillemets, spaced (« »)',
    open: `«${NNBSP}`,
    close: `${NNBSP}»`,
  },
  // German/Swiss book-style guillemets point INWARD: » opens, « closes (the reverse of French).
  { id: 'german-guillemets', label: 'German guillemets (» «)', open: '»', close: '«' },
  { id: 'finnish-swedish', label: 'Finnish/Swedish (” ”)', open: '”', close: '”' },
  { id: 'lithuanian', label: 'Lithuanian („ ”)', open: '„', close: '”' },
] as const;

const PAIRS_BY_ID: ReadonlyMap<string, QuotePair> = new Map(QUOTE_PAIRS.map((p) => [p.id, p]));

export function getQuotePair(id: string): QuotePair | undefined {
  return PAIRS_BY_ID.get(id);
}

/** Whether an id is a known, selectable pair. `""` (language default) is NOT a pair id. */
export function isKnownQuotePairId(id: string): boolean {
  return PAIRS_BY_ID.has(id);
}

// Locale (primary subtag) → default pair id, seeded from the historical per-language editor quote
// translations plus the Wikipedia quotation-mark summary. Anything unmapped falls back to English,
// matching the editor's pre-existing fallback (and fixing locales like `fi` that had no quote keys
// at all and therefore silently rendered English quotes).
const LOCALE_DEFAULT_PAIR: Readonly<Record<string, string>> = {
  en: 'english',
  de: 'german',
  fr: 'french-guillemets-spaced',
  es: 'french-guillemets',
  fi: 'finnish-swedish',
  sv: 'finnish-swedish',
  lt: 'lithuanian',
};

const FALLBACK_PAIR_ID = 'english';

function pairForLocale(locale: string | undefined | null): QuotePair {
  const subtag = (locale ?? '').toLowerCase().split(/[-_]/)[0];
  const id = LOCALE_DEFAULT_PAIR[subtag] ?? FALLBACK_PAIR_ID;
  // The map only ever names ids that exist in the table, and FALLBACK is a real id.
  return PAIRS_BY_ID.get(id) as QuotePair;
}

export interface ResolvedQuotePair extends QuotePair {
  /** True when no explicit pair was configured and the pair came from the locale fallback chain. */
  isLanguageDefault: boolean;
}

/**
 * Resolve the effective quote pair. When `quotePairId` names a known pair, that pair wins. When it
 * is `""` ("language default") — or a stale/unknown id — the pair is chosen by locale, walking the
 * liveblog's own locale, then the organization's default locale, then the editor UI language.
 */
export function resolveQuotePair(
  quotePairId: string | undefined | null,
  liveblogLocale?: string | null,
  orgLocale?: string | null,
  uiLanguage?: string | null,
): ResolvedQuotePair {
  const explicit = quotePairId ? PAIRS_BY_ID.get(quotePairId) : undefined;
  if (explicit) {
    return { ...explicit, isLanguageDefault: false };
  }
  const locale = [liveblogLocale, orgLocale, uiLanguage].find((l) => !!l && l !== '');
  return { ...pairForLocale(locale), isLanguageDefault: true };
}

// Every double-quote glyph the mechanical swap treats as replaceable input: the straight quote plus
// the curly doubles and guillemets. Single quotes / apostrophes are deliberately excluded (v1).
export const KNOWN_DOUBLE_QUOTE_GLYPHS: ReadonlySet<string> = new Set([
  '"', // U+0022
  '“', // “ left double
  '”', // ” right double
  '„', // „ low double
  '«', // « left guillemet
  '»', // » right guillemet
]);

export type QuoteRole = 'open' | 'close';

/**
 * Decide whether a quote at a given position is opening or closing purely from the character before
 * it — never from the glyph's identity, because the same glyph is opening in one orthography and
 * closing in another (`“` U+201C is English-open but German-close). Start of text, whitespace, or an
 * opening bracket → opening; otherwise closing.
 */
export function classifyBoundary(charBefore: string): QuoteRole {
  if (charBefore === '') return 'open';
  if (/\s/.test(charBefore)) return 'open';
  // Structurally-unambiguous opening context only: brackets, the Spanish inverted marks, and the
  // left single quote. Deliberately NOT the double-quote/guillemet glyphs — the same glyph opens in
  // one orthography and closes in another (“ U+201C is English-open but German-close), so a
  // preceding quote glyph cannot reliably mark an opening context.
  if ('([{¡¿‘'.includes(charBefore)) return 'open';
  return 'close';
}

/**
 * Role of the quote glyph at `index` of `text`, for a glyph the author just typed. When the effective
 * pair is itself a guillemet pair, typed guillemets are classified by NESTING like every bulk path (an
 * odd number of guillemets before it means a quotation is open, so it closes); the space before a
 * closing guillemet must not decide. Only then is counting sound: with any other pair the typed
 * opener has already been replaced by a non-guillemet mark, so no guillemet is left to count. Every
 * other case uses the preceding character, looking past only the NNBSP a spaced pair's opening mark
 * generates, so a second quote typed right after it classifies against the real preceding character.
 */
export function classifyTypedQuote(text: string, index: number, pair: Pick<QuotePair, 'open' | 'close'>): QuoteRole {
  const glyph = text.charAt(index);
  if ((glyph === '«' || glyph === '»') && /[«»]/.test(pair.open + pair.close)) {
    let guillemets = 0;
    for (let i = 0; i < index; i++) {
      const ch = text.charAt(i);
      if (ch === '«' || ch === '»') guillemets += 1;
    }
    return guillemets % 2 === 1 ? 'close' : 'open';
  }
  let b = index - 1;
  while (b >= 0 && text.charAt(b) === NNBSP) b -= 1;
  return classifyBoundary(b >= 0 ? text.charAt(b) : '');
}

// Any space that can sit INSIDE a guillemet quotation: a regular space, or the non-breaking spaces
// French typography uses. Once a guillemet's role is known (open vs close), the inner-side space is
// stripped so the chosen pair can reintroduce its own.
const GUILLEMET_INNER_SPACES: ReadonlySet<string> = new Set([' ', NNBSP, NBSP]);

export interface TypedQuoteReplacement {
  /** First character to replace: the typed glyph, or the inner spaces typed before a closing mark. */
  start: number;
  /** One past the typed glyph. */
  end: number;
  mark: string;
}

/**
 * What the editor replaces after the author typed the quote glyph at `index` of `text`: the glyph
 * becomes the pair's opening or closing mark, by `classifyTypedQuote`. A closing mark that carries its
 * own inner space (the spaced French pair) also takes the place of the inner spaces typed right before
 * it, as `reformatQuotesChunk` does, so `bonjour »` does not keep two spaces. Null when nothing changes.
 */
export function typedQuoteReplacement(
  text: string,
  index: number,
  pair: Pick<QuotePair, 'open' | 'close'>,
): TypedQuoteReplacement | null {
  const role = classifyTypedQuote(text, index, pair);
  const mark = role === 'open' ? pair.open : pair.close;
  let start = index;
  if (role === 'close' && GUILLEMET_INNER_SPACES.has(pair.close.charAt(0))) {
    while (start > 0 && GUILLEMET_INNER_SPACES.has(text.charAt(start - 1))) start -= 1;
  }
  if (start === index && mark === text.charAt(index)) return null;
  return { start, end: index + 1, mark };
}

/**
 * Whether a space typed at the caret would repeat the inner space the pair's opening mark already
 * ends with (the spaced French pair), so the editor drops it.
 */
export function absorbsTypedSpace(textBeforeCaret: string, pair: Pick<QuotePair, 'open' | 'close'>): boolean {
  return GUILLEMET_INNER_SPACES.has(pair.open.charAt(pair.open.length - 1)) && textBeforeCaret.endsWith(pair.open);
}

/**
 * Mutable state for scanning quotes across chunks (e.g. Trix text nodes). `guillemetOpen` tracks
 * whether we are inside a « » quotation so the NEXT guillemet closes it; `lastChar` is the last source
 * character, used to classify a straight/curly quote at a chunk boundary.
 */
export interface QuoteScanState {
  guillemetOpen: boolean;
  lastChar: string;
}

/**
 * Replace known double-quote glyphs in one text chunk with the pair's marks, updating `state`.
 *
 * Guillemets (« ») are classified by open/close NESTING (first = opening, next = closing, …), NOT by
 * glyph or adjacent space: French writes «…» and German writes reversed »…«, so the glyph is
 * ambiguous, and the closing mark can be preceded by inner spacing (which position-based
 * classification would misread as opening). Once the role is known the inner-side space (regular or
 * non-breaking) is stripped and the pair's own spacing applied — so French « bonjour » (regular or
 * NNBSP inner spaces) and German reversed »mehr« all convert correctly. Straight/curly quotes keep the
 * positional heuristic.
 */
export function reformatQuotesChunk(text: string, pair: QuotePair, state: QuoteScanState): string {
  let out = '';
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '«' || ch === '»') {
      const opening = !state.guillemetOpen;
      state.guillemetOpen = opening;
      if (opening) {
        out += pair.open;
        while (i + 1 < text.length && GUILLEMET_INNER_SPACES.has(text[i + 1])) i += 1;
      } else {
        while (out.length > 0 && GUILLEMET_INNER_SPACES.has(out[out.length - 1])) out = out.slice(0, -1);
        out += pair.close;
      }
      state.lastChar = ch;
    } else if (KNOWN_DOUBLE_QUOTE_GLYPHS.has(ch)) {
      const before = i > 0 ? text[i - 1] : state.lastChar;
      out += classifyBoundary(before) === 'open' ? pair.open : pair.close;
      state.lastChar = ch;
    } else {
      out += ch;
      state.lastChar = ch;
    }
  }
  return out;
}

/**
 * Replace every known double-quote glyph in a plain-text string with the pair's marks. Used by the
 * HTML/MCP (non-editor) path and by tests; the editor drives the same `reformatQuotesChunk` across
 * Trix text nodes to keep undo intact.
 */
export function reformatQuotesInText(text: string, pair: QuotePair): string {
  return reformatQuotesChunk(text, pair, { guillemetOpen: false, lastChar: '' });
}

/**
 * Normalize line breaks and paste whitespace in a plain-text string: strip leading/trailing blank
 * lines, collapse three-or-more consecutive line breaks to a single blank-line paragraph separator,
 * strip trailing per-line whitespace, and collapse runs of spaces / regular non-breaking spaces
 * introduced by pasting. A single blank line (paragraph separator) is preserved. The narrow
 * no-break space (U+202F) used for guillemet spacing is left untouched.
 */
export function normalizeText(text: string): string {
  let out = text.replace(/\r\n?/g, '\n');
  // Collapse runs of ordinary spaces, tabs, and regular NBSP (paste artifacts) to a single space;
  // then drop any remaining lone NBSP. U+202F is intentionally not in these classes. NBSP/TAB come
  // from char codes so no irregular-whitespace character ever appears in the source regex.
  const nbsp = String.fromCharCode(0xa0);
  const tab = String.fromCharCode(9);
  out = out.replace(new RegExp('[ ' + tab + nbsp + ']{2,}', 'g'), ' ');
  out = out.split(nbsp).join(' ');
  // Trailing whitespace at the end of each line.
  out = out.replace(/[ \t]+$/gm, '');
  // 3+ line breaks collapse to a single blank line (i.e. exactly one empty line between paragraphs).
  out = out.replace(/\n{3,}/g, '\n\n');
  // Leading and trailing blank lines.
  out = out.replace(/^(?:[ \t]*\n)+/, '');
  out = out.replace(/(?:\n[ \t]*)+$/, '');
  return out;
}
